import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";

// Editorial horizontal marquee — five keywords glide across in an
// infinite ribbon. The last one, Crecimiento, always lands in orange as
// the emphatic close. A second, smaller row drifts the opposite way to
// add depth without noise.

const WORDS = ["Confianza", "Claridad", "Conversión", "Estrategia", "Crecimiento"];

// Cat paw ornament placed between words — pulses (appears/disappears)
// with a staggered offset so the ribbon feels alive.
const Ornament = ({ color = "currentColor", delay = 0 }) => (
  <motion.svg
    width="26"
    height="26"
    viewBox="0 0 24 24"
    className="mx-8 lg:mx-14 shrink-0"
    aria-hidden="true"
    animate={{ opacity: [0.15, 0.9, 0.15], scale: [0.85, 1.05, 0.85] }}
    transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut", delay }}
  >
    <g fill={color}>
      {/* four toe beans */}
      <ellipse cx="6.5" cy="9" rx="1.7" ry="2.3" />
      <ellipse cx="10.5" cy="6" rx="1.7" ry="2.3" />
      <ellipse cx="15.5" cy="6" rx="1.7" ry="2.3" />
      <ellipse cx="17.5" cy="10" rx="1.7" ry="2.3" />
      {/* main pad */}
      <path d="M8.5 18 C 6.5 14.5, 17.5 14.5, 15.5 18 C 14.4 20, 9.6 20, 8.5 18 Z" />
    </g>
  </motion.svg>
);

// Directional infinite marquee row (CSS keyframes → smooth, GPU-friendly).
// overflow-x-hidden clips the horizontal loop illusion; vertical is left
// open so descenders (g, y, j, p, etc.) never get cut off.
function MarqueeRow({ direction = "left", duration = 48, italic = false, className = "" }) {
  const animName = direction === "left" ? "michi-marquee-left" : "michi-marquee-right";
  return (
    <div className={`relative overflow-x-hidden py-[0.2em] ${className}`}>
      <div
        className="flex items-center whitespace-nowrap will-change-transform"
        style={{
          animation: `${animName} ${duration}s linear infinite`,
        }}
      >
        {/* Track duplicated 2x so the loop is seamless */}
        {[0, 1].map((track) => (
          <div key={track} className="flex items-center shrink-0" aria-hidden={track === 1}>
            {WORDS.map((w, i) => {
              const isLast = i === WORDS.length - 1;
              // staggered delays so each paw pulses out-of-phase
              const pawDelay = ((track * WORDS.length + i) * 0.35) % 2.4;
              return (
                <span key={`${track}-${w}`} className="flex items-center shrink-0">
                  <span
                    className={`inline-block ${italic ? "font-italic-display" : "font-display"} tightest leading-[1.18] pb-[0.05em]`}
                    style={{ color: isLast ? "#FF6B2C" : undefined }}
                  >
                    {w}
                  </span>
                  <Ornament color={isLast ? "#FF6B2C" : "#111"} delay={pawDelay} />
                </span>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}

export default function TypographicTransition() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });

  // Fine, scroll-linked extra drift so the ribbon breathes with the user.
  const drift = useTransform(scrollYProgress, [0, 1], [-40, 40]);
  const drift2 = useTransform(scrollYProgress, [0, 1], [40, -40]);

  return (
    <section
      ref={ref}
      data-testid="typographic-transition"
      className="relative py-10 lg:py-14 overflow-x-hidden"
      aria-label="Confianza, claridad, conversión, estrategia, crecimiento"
    >
      {/* Editorial index label */}
      <div className="max-w-[95%] mx-auto mb-6 lg:mb-8 flex items-center justify-between font-sans text-[11px] tracking-[0.24em] uppercase text-[color:var(--ink-60)]">
        <span>— Interludio · Cinco palabras</span>
        <span className="hidden md:inline italic normal-case font-italic-display text-base tracking-normal text-[color:var(--ink)]/50">
          el método, en cinco compases
        </span>
      </div>

      {/* Thin hairline top */}
      <div className="max-w-[95%] mx-auto h-px bg-[color:var(--ink-10)] mb-6 lg:mb-8" />

      {/* Big marquee (moves left) */}
      <motion.div
        style={{ x: drift }}
        className="text-[12vw] lg:text-[8.4vw]"
      >
        <MarqueeRow direction="left" duration={22} italic={false} />
      </motion.div>

      {/* Subtle secondary row (italic, smaller, drifts right, faded) */}
      <motion.div
        style={{ x: drift2 }}
        className="mt-3 lg:mt-5 opacity-40 text-[6vw] lg:text-[3.4vw]"
      >
        <MarqueeRow direction="right" duration={30} italic={true} />
      </motion.div>

      {/* Thin hairline bottom */}
      <div className="max-w-[95%] mx-auto h-px bg-[color:var(--ink-10)] mt-6 lg:mt-8" />

      {/* Global keyframes for the marquee */}
      <style>{`
        @keyframes michi-marquee-left {
          0%   { transform: translate3d(0, 0, 0); }
          100% { transform: translate3d(-50%, 0, 0); }
        }
        @keyframes michi-marquee-right {
          0%   { transform: translate3d(-50%, 0, 0); }
          100% { transform: translate3d(0, 0, 0); }
        }
        @media (prefers-reduced-motion: reduce) {
          [style*="michi-marquee-"] { animation: none !important; }
        }
      `}</style>
    </section>
  );
}
